import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import api from '../services/api';
import type { Product } from '../types';
import { formatMoney } from '../components/commercial/contact';

type PriceList = { id: number; name: string; adjustmentPct: number; isDefault: boolean; items: { productId: number; price: number }[]; customers: number };
type Combo = { productId: number; productName: string; components: { componentProductId: number; componentName: string; quantity: number }[] };

/** Listas de precios por canal (Distribuidor, Local, PVP, Farmacity…) y combos (DUO, TRIO…). */
const PriceListsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [lists, setLists] = useState<PriceList[]>([]);
  const [combos, setCombos] = useState<Combo[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ name: string; adjustmentPct: number; isDefault: boolean; prices: Record<number, string> }>({ name: '', adjustmentPct: 0, isDefault: false, prices: {} });
  const [comboProduct, setComboProduct] = useState<number | ''>('');
  const [comboDraft, setComboDraft] = useState<{ componentProductId: number | ''; quantity: number }[]>([]);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    const [p, l, c] = await Promise.all([
      api.getProducts({ page: 1, pageSize: 500 }),
      api.getPriceLists(),
      api.getCombos(),
    ]);
    setProducts((p.products || []).filter(x => x.isActive !== false));
    setLists(l);
    setCombos(c);
  }, []);

  useEffect(() => { load().catch(() => setMsg({ type: 'error', text: 'No se pudo cargar' })); }, [load]);

  const comboIds = useMemo(() => new Set(combos.map(c => c.productId)), [combos]);

  const pick = (l: PriceList | null) => {
    setSelected(l?.id ?? 0);
    setDraft(l
      ? { name: l.name, adjustmentPct: l.adjustmentPct, isDefault: l.isDefault, prices: Object.fromEntries(l.items.map(i => [i.productId, String(i.price)])) }
      : { name: '', adjustmentPct: 0, isDefault: false, prices: {} });
  };

  const saveList = async () => {
    try {
      if (!draft.name.trim()) { setMsg({ type: 'error', text: 'Poné un nombre a la lista' }); return; }
      let id = selected || 0;
      const body = { name: draft.name, adjustmentPct: Number(draft.adjustmentPct) || 0, isDefault: draft.isDefault };
      if (id) await api.updatePriceList(id, body);
      else id = (await api.createPriceList(body)).id;
      await api.setPriceListItems(id, Object.entries(draft.prices)
        .filter(([, v]) => Number(v) > 0)
        .map(([pid, v]) => ({ productId: Number(pid), price: Number(v) })));
      setMsg({ type: 'success', text: 'Lista guardada' });
      await load();
      setSelected(id);
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'No se pudo guardar la lista' });
    }
  };

  const removeList = async (id: number) => {
    await api.deletePriceList(id);
    setSelected(null);
    load();
  };

  const pickCombo = (pid: number | '') => {
    setComboProduct(pid);
    const c = combos.find(x => x.productId === pid);
    setComboDraft(c ? c.components.map(x => ({ componentProductId: x.componentProductId, quantity: x.quantity })) : [{ componentProductId: '', quantity: 1 }]);
  };

  const saveCombo = async () => {
    if (!comboProduct) return;
    try {
      await api.setCombo(Number(comboProduct), comboDraft
        .filter(c => c.componentProductId && c.quantity > 0)
        .map(c => ({ componentProductId: Number(c.componentProductId), quantity: Number(c.quantity) })));
      setMsg({ type: 'success', text: 'Combo guardado' });
      load();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'No se pudo guardar el combo' });
    }
  };

  const listPreview = (p: Product) => {
    const fixed = Number(draft.prices[p.id]);
    if (fixed > 0) return fixed;
    return Math.round(p.price * (1 + (Number(draft.adjustmentPct) || 0) / 100) * 100) / 100;
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>Precios y combos</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Listas de precios" />
        <Tab label="Combos" />
      </Tabs>
      {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg.text}</Alert>}

      {tab === 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Card variant="outlined" sx={{ minWidth: 260, width: { xs: '100%', md: 280 } }}>
            <CardContent>
              <Stack spacing={1}>
                {lists.map(l => (
                  <Button key={l.id} variant={selected === l.id ? 'contained' : 'outlined'} onClick={() => pick(l)} sx={{ justifyContent: 'space-between' }}>
                    <span>{l.name}</span>
                    <span>{l.isDefault ? '★ ' : ''}{l.customers} clientes</span>
                  </Button>
                ))}
                <Button startIcon={<Add />} onClick={() => pick(null)}>Nueva lista</Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Cada cliente usa su lista (se elige en la ficha del cliente) o la marcada con ★. El precio pactado del cliente gana sobre la lista.
              </Typography>
            </CardContent>
          </Card>

          {selected !== null && (
            <Card variant="outlined" sx={{ flex: 1, width: '100%' }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                  <TextField label="Nombre" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Distribuidor" />
                  <TextField label="Ajuste sobre precio de lista %" type="number" value={draft.adjustmentPct}
                    onChange={e => setDraft({ ...draft, adjustmentPct: Number(e.target.value) })} helperText="-35 = 35% abajo" sx={{ width: 220 }} />
                  <FormControlLabel control={<Switch checked={draft.isDefault} onChange={e => setDraft({ ...draft, isDefault: e.target.checked })} />} label="Por defecto" />
                  <Box flex={1} />
                  {!!selected && <IconButton color="error" onClick={() => removeList(selected)} aria-label="Borrar lista"><Delete /></IconButton>}
                  <Button variant="contained" startIcon={<Save />} onClick={saveList}>Guardar</Button>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="right">Precio de lista</TableCell>
                      <TableCell align="right">Precio fijo en esta lista</TableCell>
                      <TableCell align="right">Queda en</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell align="right">{formatMoney(p.price)}</TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number" value={draft.prices[p.id] ?? ''} sx={{ width: 120 }}
                            onChange={e => setDraft({ ...draft, prices: { ...draft.prices, [p.id]: e.target.value } })} />
                        </TableCell>
                        <TableCell align="right"><b>{formatMoney(listPreview(p))}</b></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Un combo (DUO, TRIO, Classic Care…) no tiene stock propio: al venderlo —en el sistema o por Tiendanube— se descuentan sus componentes, del lote más viejo primero.
              </Typography>
              <Stack spacing={2}>
                <TextField select label="Producto combo" value={comboProduct} onChange={e => pickCombo(e.target.value === '' ? '' : Number(e.target.value))} sx={{ maxWidth: 400 }}>
                  {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}{comboIds.has(p.id) ? ' (combo)' : ''}</MenuItem>)}
                </TextField>
                {comboProduct !== '' && (
                  <>
                    {comboDraft.map((c, idx) => (
                      <Stack key={idx} direction="row" spacing={1} alignItems="center">
                        <TextField select size="small" label="Componente" value={c.componentProductId} sx={{ minWidth: 260 }}
                          onChange={e => setComboDraft(d => d.map((x, i) => (i === idx ? { ...x, componentProductId: Number(e.target.value) } : x)))}>
                          {products.filter(p => p.id !== comboProduct && !comboIds.has(p.id)).map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                        </TextField>
                        <TextField size="small" type="number" label="Cantidad" value={c.quantity} sx={{ width: 110 }}
                          onChange={e => setComboDraft(d => d.map((x, i) => (i === idx ? { ...x, quantity: Number(e.target.value) } : x)))} />
                        <IconButton onClick={() => setComboDraft(d => d.filter((_, i) => i !== idx))} aria-label="Quitar"><Delete /></IconButton>
                      </Stack>
                    ))}
                    <Stack direction="row" spacing={1}>
                      <Button startIcon={<Add />} onClick={() => setComboDraft(d => [...d, { componentProductId: '', quantity: 1 }])}>Agregar componente</Button>
                      <Button variant="contained" startIcon={<Save />} onClick={saveCombo}>Guardar combo</Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
          {combos.map(c => (
            <Card key={c.productId} variant="outlined">
              <CardContent>
                <Typography fontWeight={700}>{c.productName}</Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  {c.components.map(x => <Chip key={x.componentProductId} label={`${x.quantity} × ${x.componentName}`} />)}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PriceListsPage;
